import { format } from "date-fns";
import { startTransition, useState } from "react";
import { DateRange } from "react-day-picker";

import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import Button from "hardcover-ui/components/Button";
import Icon from "hardcover-ui/components/Icon";
import { ButtonThemeProps } from "hardcover-ui/theme/components/button";
import calendar from "icons/regular/calendar.svg";
import faTrash from "icons/solid/trash.svg";
import { cn } from "lib/utils";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  startDate?: Date;
  endDate?: Date;
  onChange: any;
  onDelete?: any;
  showCalendarIcon?: boolean;
  variant?: ButtonThemeProps["variant"];
  size?: ButtonThemeProps["size"];
}

function PickDateRange({ date }: { date?: DateRange }) {
  if (!date || (!date.from && !date.to)) {
    return <span>Pick a date</span>;
  }

  return (
    <span>
      {date?.from ? format(date.from, "LLL dd, y") : "?"} -{" "}
      {date?.to ? format(date.to, "LLL dd, y") : "?"}
    </span>
  );
}
export function DatePickerWithRange({
  className = "",
  startDate,
  endDate,
  onChange,
  onDelete,
  showCalendarIcon = true,
  size = "none",
  variant = "none",
}: Props) {
  const [date, setDate] = useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });

  const changeDate = (newDate: DateRange) => {
    startTransition(() => {
      setDate(newDate);
      onChange(newDate);
    });
  };

  const handleDayClick = (day: Date) => {
    const dayTime = day.getTime();
    const hasFrom = date?.from;
    const hasTo = date?.to;
    const fromTime = date?.from?.getTime();
    const toTime = date?.to?.getTime();

    let newDate: DateRange;

    if (!hasFrom && !hasTo) {
      // First click, set from to day
      newDate = {
        from: day,
        to: undefined,
      };
    } else if (hasFrom && !hasTo && fromTime === dayTime) {
      // Second click, already set from
      // Clicked same thing, Toggle From -> To
      newDate = {
        from: undefined,
        to: day,
      };
    } else if (
      hasFrom &&
      hasTo &&
      fromTime === toTime &&
      fromTime === dayTime
    ) {
      // Third click, already set from and to
      // Clicked same thing, Set both to undefined
      newDate = {
        from: undefined,
        to: undefined,
      };
    } else if (!hasFrom && hasTo && toTime === dayTime) {
      // Second click, already set from
      // Clicked same thing, Toggle Both -> undefined
      newDate = {
        from: day,
        to: day,
      };
    } else if (hasFrom && !hasTo && dayTime > fromTime) {
      // Second click, on a future date
      newDate = {
        from: date.from,
        to: day,
      };
    } else if (hasFrom && !hasTo && dayTime < fromTime) {
      // Second click, on a past date
      newDate = {
        from: day,
        to: date.from,
      };
    } else if (hasFrom && hasTo && dayTime < fromTime) {
      newDate = {
        from: day,
        to: date.to,
      };
    } else if (hasTo && !hasFrom && dayTime < toTime) {
      newDate = {
        from: day,
        to: date.to,
      };
    } else if (hasTo && hasFrom && dayTime === toTime && dayTime === fromTime) {
      // Already has a range, clicked on the start date
      newDate = {
        from: undefined,
        to: undefined,
      };
    } else if (hasTo && hasFrom && dayTime === toTime) {
      // Already has a range, clicked on the end date
      newDate = {
        from: day,
        to: day,
      };
    } else {
      newDate = {
        from: date.from,
        to: day,
      };
    }

    if (newDate) {
      changeDate(newDate);
    }
  };

  const deleteRead = () => {
    if (onDelete) {
      if (window.confirm("Are you sure you want to delete this read?")) {
        onDelete();
      }
    } else {
      setDate({
        from: undefined,
        to: undefined,
      });
    }
  };

  const endDateYear = new Date().getFullYear() + 1;

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={variant}
            size={size}
            className={cn(
              "py-1 px-2 rounded-sm justify-start text-left font-normal items-center",
              !date && "text-muted-foreground",
            )}
          >
            {showCalendarIcon ? (
              <Icon icon={calendar} className="mr-2 h-4 w-4" />
            ) : (
              false
            )}
            <PickDateRange date={date} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 z-100 overflow-hidden"
          align="start"
        >
          <Calendar
            initialFocus
            fixedWeeks
            mode="range"
            defaultMonth={date?.from || date?.to}
            selected={date}
            onDayClick={handleDayClick}
            numberOfMonths={1}
            captionLayout="dropdown-buttons"
            fromYear={1900}
            toYear={endDateYear}
            className="bg-white dark:bg-gray-900 "
          />

          <div className="flex flex-row space-x-2 justify-between p-1 bg-white dark:bg-gray-900">
            <Button
              variant="dangerLink"
              className="grow text-center px-2"
              onClick={deleteRead}
            >
              <Icon icon={faTrash} className="w-3 h-3" size="md" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="grow text-center"
              onClick={() => {
                const now = new Date();
                changeDate({
                  from: now,
                  to: date?.to,
                });
              }}
            >
              Started Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="grow text-center"
              onClick={() => {
                const now = new Date();
                changeDate({
                  from: date?.from,
                  to: now,
                });
              }}
            >
              Finished Today
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
